import React, { useState, useEffect } from 'react';
import { query, collection, getDocs, getDoc, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { Modal, Button, ProgressBar } from 'react-bootstrap';
import EmailViewModal from './EmailViewModal';
import { listenToMailSourceMode, normalizeMailSource, type MailSourceMode } from '../../../utils/mailSourceConfig';

interface CustomerEmailsProps {
  customerId: string | null;
  onEmailSaved: () => void;
}

interface From {
  address: string;
  text: string;
}

interface Email {
  id: string;
  from: From[];
  subject: string;
  text: string;
  customerId?: string | null;
  formattedDate?: string;
  date: Timestamp;
}

interface Contact {
  email: string;
  name: string;
  phone: string;
}

interface CustomerData {
  email?: string;
  name: string;
  contacts?: Contact[];
}

const CustomerEmails: React.FC<CustomerEmailsProps> = ({ customerId, onEmailSaved }) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<Email | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [processingEmails, setProcessingEmails] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [mailSourceMode, setMailSourceMode] = useState<MailSourceMode>('both');

  useEffect(() => {
    const unsub = listenToMailSourceMode((mode) => setMailSourceMode(mode));
    return () => unsub();
  }, []);

  const handleSaveEmail = async (email: Email) => {
    try {
      if (!customerId) return;

      const emailRef = doc(db, 'emails', email.id);
      
      if (email.customerId) {
        await updateDoc(emailRef, {
          customerId: null
        });
        
        setEmails(emails.map(e => 
          e.id === email.id ? { ...e, customerId: null } : e
        ));
      } else {
        await updateDoc(emailRef, {
          customerId: customerId
        });
        
        setEmails(emails.map(e => 
          e.id === email.id ? { ...e, customerId: customerId } : e
        ));
      }

      onEmailSaved();
    } catch (error) {
      console.error("Error saving email:", error);
    }
  };

  const handleDeleteClick = (email: Email) => {
    setEmailToDelete(email);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (emailToDelete) {
      try {
        await deleteDoc(doc(db, 'emails', emailToDelete.id));
        setEmails(emails.filter(e => e.id !== emailToDelete.id));
        setShowDeleteModal(false);
        setEmailToDelete(null);
      } catch (error) {
        console.error("Error deleting email:", error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setEmailToDelete(null);
  };

  const handleEmailClick = (email: Email) => {
    setSelectedEmail(email);
    setShowEmailModal(true);
  };

  useEffect(() => {
    const fetchEmails = async () => {
      if (!customerId) return;

      setIsLoading(true);
      setProcessingEmails(false);
      setProcessedCount(0);
      setTotalToProcess(0);
      
      try {
        const customerDoc = await getDoc(doc(db, 'customers', customerId));
        const customerData = customerDoc.data() as CustomerData;

        if (customerData) {
          const customerEmails = new Set<string>();
          
          if (customerData.email) {
            customerEmails.add(customerData.email.toLowerCase());
          }
          
          if (customerData.contacts) {
            customerData.contacts.forEach((contact: Contact) => {
              if (contact.email) {
                customerEmails.add(contact.email.toLowerCase());
              }
            });
          }

          const emailsQuery = collection(db, 'emails');
          const querySnapshot = await getDocs(emailsQuery);

          // First, get all matching emails and display them
          const matchedEmails = querySnapshot.docs
            .filter(doc => {
              const emailData: any = doc.data();
              const src = normalizeMailSource(emailData?.source);
              if (mailSourceMode === 'graph' && src !== 'graph') return false;
              if (mailSourceMode === 'imap' && src === 'graph') return false;
              return emailData.from.some((from: From) =>
                customerEmails.has(from.address.toLowerCase())
              );
            })
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
              customerId: doc.data().customerId || null
            } as Email));

          // Set initial emails state
          setEmails(matchedEmails.sort((a, b) => b.date.seconds - a.date.seconds));
          setIsLoading(false);

          // Then process updates asynchronously
          const emailsToUpdate = matchedEmails.filter(email => !email.customerId);
          setTotalToProcess(emailsToUpdate.length);
          
          if (emailsToUpdate.length > 0) {
            setProcessingEmails(true);
            
            for (let i = 0; i < emailsToUpdate.length; i++) {
              const email = emailsToUpdate[i];
              const emailRef = doc(db, 'emails', email.id);
              
              await updateDoc(emailRef, {
                customerId: customerId
              });

              setProcessedCount(i + 1);
              
              // Update the emails state to reflect the change
              setEmails(prevEmails => 
                prevEmails.map(e => 
                  e.id === email.id ? { ...e, customerId: customerId } : e
                )
              );

              // Small delay to prevent overwhelming the database
              await new Promise(resolve => setTimeout(resolve, 100));
            }

            setProcessingEmails(false);
            onEmailSaved();
          }
        }
      } catch (error) {
        console.error("Error fetching and updating emails:", error);
        setProcessingEmails(false);
      }
    };

    fetchEmails();
  }, [customerId, onEmailSaved, mailSourceMode]);

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container mt-3">
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between mb-3">
            <h5>Customer Emails</h5>
          </div>

          {processingEmails && (
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <small>Processing emails: {processedCount} of {totalToProcess}</small>
                <small>{Math.round((processedCount / totalToProcess) * 100)}%</small>
              </div>
              <ProgressBar
                now={(processedCount / totalToProcess) * 100}
                animated
                variant="primary"
              />
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <table className="table table-hover">
              <thead>
                <tr>
                  <th className="fs-6"><b>Date</b></th>
                  <th className="fs-6"><b>Subject</b></th>
                  <th className="fs-6"><b>Content</b></th>
                  <th className="fs-6"><b>Actions</b></th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email, index) => (
                  <tr key={index} 
                      onClick={() => handleEmailClick(email)}
                      style={{ cursor: 'pointer' }}>
                    <td>{formatDate(email.date)}</td>
                    <td>{email.subject}</td>
                    <td>
                      {email.text ? (
                        email.text.length > 150 
                          ? email.text.substring(0, 150).replace(/\s+\S*$/, '') + '...' 
                          : email.text
                      ) : 'No body'}
                    </td>
                    <td>
                      <button 
                        className="btn btn-link p-0 me-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveEmail(email);
                        }}
                        title={email.customerId ? "Unsave Email" : "Save Email"}
                      >
                        <i className={`bi ${email.customerId ? 'bi-bookmark-fill' : 'bi-bookmark'} fs-3`}></i>
                      </button>
                      <button 
                        className="btn btn-link p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(email);
                        }}
                        title="Delete Email"
                      >
                        <i className="bi bi-trash fs-3"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <EmailViewModal
            email={selectedEmail}
            show={showEmailModal}
            onHide={() => setShowEmailModal(false)}
          />

          <Modal show={showDeleteModal} onHide={handleDeleteCancel}>
            <Modal.Header closeButton>
              <Modal.Title>Confirm Delete</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to delete this email?
              {emailToDelete && (
                <div className="mt-2">
                  <strong>Subject:</strong> {emailToDelete.subject}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleDeleteCancel}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default CustomerEmails;